import React, { useState } from 'react';
import {
  Box, IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { getConsultStats } from '../../api/consultManage';
import { CONSULT_METHOD_LABELS, CONSULT_RESULT_COLORS, CONSULT_RESULT_LABELS } from '../../constants/consultation';
import { VISIT_PATH_LABELS } from '../../constants/visitPath';
import { errorMessages } from '../../utils/methods';
import { Card, DailyLineChart, DonutChart, Empty, SummaryCard } from './ClassAttendanceStats';

// 수업·상담 관리 > 통계 > 상담. 상담일 기준 월 단위.
// 등록률 = (신규등록 + 재등록) / 전체 상담.
const COLORS = {
  total: '#262626',
  registered: '#1F7343',
  newRegistered: '#1F7343',
  reRegistered: '#5B21B6',
  unconfirmed: '#6B7280',
  noShow: CONSULT_RESULT_COLORS.NO_SHOW,
};

const percent = (part, total) => (total ? Math.round((part / total) * 1000) / 10 : 0);
const NONE_LABEL = '미입력';

const headSx = { fontWeight: 600, background: '#F7F9FA', whiteSpace: 'nowrap' };

function ConsultStats({ branchId }) {
  const user = useSelector(state => state.user.user);
  const [month, setMonth] = useState(() => dayjs().startOf('month'));
  const [managerKey, setManagerKey] = useState('');
  const monthKey = month.format('YYYY-MM');
  const isCurrentMonth = month.isSame(dayjs(), 'month');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['consultStats', branchId, monthKey],
    queryFn: () => getConsultStats(branchId, monthKey, user.token),
    enabled: !!branchId && !!user?.token,
  });

  const s = data?.summary;
  const p = data?.previousSummary;
  const registered = c => (c ? c.newRegistered + c.reRegistered : 0);
  const currentLabel = `${month.month() + 1}월`;
  const previousLabel = `${month.subtract(1, 'month').month() + 1}월`;
  const today = dayjs().date();
  const slice = list => list.slice(0, isCurrentMonth ? today : list.length);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
        <IconButton size="small" onClick={() => { setMonth(m => m.subtract(1, 'month')); setManagerKey(''); }} aria-label="이전 달" sx={{ border: '1px solid #E0E0E0' }}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#262626', minWidth: 150, textAlign: 'center' }}>
          {month.format('YYYY년 M월')}
        </Typography>
        <IconButton
          size="small" disabled={isCurrentMonth} onClick={() => { setMonth(m => m.add(1, 'month')); setManagerKey(''); }}
          aria-label="다음 달" sx={{ border: '1px solid #E0E0E0' }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <Typography sx={{ fontSize: '12px', color: '#999', textAlign: 'center', mt: '-8px' }}>
        상담일 기준 · 등록률 = 신규·재등록 / 전체 상담
      </Typography>

      {isError && <Typography sx={{ color: '#d32f2f', textAlign: 'center' }}>{errorMessages(error)}</Typography>}
      {isLoading && <Typography sx={{ color: '#888', textAlign: 'center' }}>불러오는 중...</Typography>}

      {data && (
        <React.Fragment key={monthKey}>
          {/* 카드 6개 = 3열 2줄(한 줄 5개 + 넘친 1개가 통째로 늘어나지 않게) */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: '12px' }}>
            <SummaryCard label="총 상담" color={COLORS.total} value={s.total} previous={p?.total} good />
            <SummaryCard
              label="등록(신규+재등록)" color={COLORS.registered} value={registered(s)} total={s.total}
              previous={registered(p)} previousTotal={p?.total} good
            />
            <SummaryCard
              label="신규등록" color={COLORS.newRegistered} value={s.newRegistered} total={s.total}
              previous={p?.newRegistered} previousTotal={p?.total} good
            />
            <SummaryCard
              label="재등록" color={COLORS.reRegistered} value={s.reRegistered} total={s.total}
              previous={p?.reRegistered} previousTotal={p?.total} good
            />
            <SummaryCard
              label="미확인(결과 미입력)" color={COLORS.unconfirmed} value={s.unconfirmed} total={s.total}
              previous={p?.unconfirmed} previousTotal={p?.total}
            />
            <SummaryCard
              label="미방문" color={COLORS.noShow} value={s.noShow} total={s.total}
              previous={p?.noShow} previousTotal={p?.total}
            />
          </Box>
          <Typography sx={{ fontSize: '12px', color: '#999', mt: '-6px' }}>
            미확인을 뺀 등록률 {percent(registered(s), s.total - s.unconfirmed)}%
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: '12px' }}>
            <Card title="일별 상담 현황">
              <DailyLineChart
                current={slice(data.daily).map(d => d.counts.total)}
                previous={data.previousDaily.map(d => d.counts.total)}
                color={COLORS.total} currentLabel={currentLabel} previousLabel={previousLabel}
              />
            </Card>
            <Card title="결과 분포" center>
              <DonutChart
                items={[
                  ['SCHEDULED', s.pending], ['UNCONFIRMED', s.unconfirmed], ['NO_SHOW', s.noShow], ['VISITED', s.visited],
                  ['CONSIDERING', s.considering], ['NOT_REGISTERED', s.notRegistered],
                  ['NEW_REGISTERED', s.newRegistered], ['RE_REGISTERED', s.reRegistered],
                ].filter(([, v]) => v > 0).map(([code, value]) => ({
                  key: code, name: CONSULT_RESULT_LABELS[code], value, color: CONSULT_RESULT_COLORS[code],
                }))}
              />
            </Card>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: '12px' }}>
            <Card title="일별 등록 현황">
              <DailyLineChart
                current={slice(data.daily).map(d => d.counts.newRegistered + d.counts.reRegistered)}
                previous={data.previousDaily.map(d => d.counts.newRegistered + d.counts.reRegistered)}
                color={COLORS.registered} currentLabel={currentLabel} previousLabel={previousLabel}
              />
            </Card>
            <Card title="담당자별 등록" center>
              <DonutChart
                items={data.managers
                  .map(g => ({ key: g.key, name: g.name, value: g.counts.newRegistered + g.counts.reRegistered }))
                  .filter(i => i.value > 0)
                  .sort((a, b) => b.value - a.value)}
              />
            </Card>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '12px' }}>
            <Card title="상담 수단별" center>
              <DonutChart
                items={data.methods.map(g => ({
                  key: g.key, name: g.key === 'NONE' ? NONE_LABEL : CONSULT_METHOD_LABELS[g.key] || g.key, value: g.counts.total,
                }))}
              />
            </Card>
            <Card title="방문경로별" center>
              <DonutChart
                items={data.visitPaths.map(g => ({
                  key: g.key, name: g.key === 'NONE' ? NONE_LABEL : VISIT_PATH_LABELS[g.key] || g.key, value: g.counts.total,
                }))}
              />
            </Card>
          </Box>

          <Card>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mb: 2, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#262626' }}>담당자별 성과</Typography>
              <TextField
                select size="small" label="담당자" sx={{ minWidth: 160 }}
                value={managerKey} onChange={e => setManagerKey(e.target.value)}>
                <MenuItem value="">전체</MenuItem>
                {data.managers.map(g => (
                  <MenuItem key={g.key} value={g.key}>{g.name}</MenuItem>
                ))}
              </TextField>
            </Box>
            {data.managers.length === 0 ? <Empty /> : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['담당자', '전체', '신규등록', '재등록', '미등록·고민', '미방문', '미확인', '등록률', '미확인률'].map(h => (
                      <TableCell key={h} align="center" sx={headSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.managers.filter(g => !managerKey || g.key === managerKey).map(g => {
                    const c = g.counts;
                    return (
                      <TableRow key={g.key}>
                        <TableCell align="center">{g.name}</TableCell>
                        <TableCell align="center">{c.total}</TableCell>
                        <TableCell align="center">{c.newRegistered}</TableCell>
                        <TableCell align="center">{c.reRegistered}</TableCell>
                        <TableCell align="center">{c.notRegistered + c.considering}</TableCell>
                        <TableCell align="center">{c.noShow}</TableCell>
                        <TableCell align="center">{c.unconfirmed}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: COLORS.registered }}>
                          {percent(c.newRegistered + c.reRegistered, c.total)}%
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: '#D32F2F' }}>
                          {percent(c.unconfirmed, c.total)}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </React.Fragment>
      )}
    </Box>
  );
}

export default ConsultStats;
